import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { env } from '@/lib/env';

export interface NativeGoogleSignInResult {
  idToken: string;
  nonce: string;
  accessToken?: string | null;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
}

export interface NativeGoogleSignInOptions {
  forceAccountSelection?: boolean;
}

const WEB_CLIENT_ID = env.googleWebClientId?.trim() || '';
const IOS_CLIENT_ID = env.googleIosClientId?.trim() || '';

let initializePromise: Promise<void> | null = null;

export function isNativeGoogleSignInRuntime(): boolean {
  return Capacitor.isNativePlatform() && ['android', 'ios'].includes(Capacitor.getPlatform());
}

/**
 * Native Google sign-in requires per-platform OAuth client IDs. When they are
 * not configured the caller falls back to the browser OAuth flow, so a missing
 * ID must never throw here.
 */
export function isNativeGoogleSignInConfigured(): boolean {
  if (!isNativeGoogleSignInRuntime()) return false;
  if (Capacitor.getPlatform() === 'ios') return Boolean(WEB_CLIENT_ID && IOS_CLIENT_ID);
  return Boolean(WEB_CLIENT_ID);
}

export function isNativeGoogleSignInCanceled(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const normalized = message.toLowerCase();
  return (
    normalized.includes('cancel') ||
    normalized.includes('12501') ||
    normalized.includes('user closed') ||
    normalized.includes('interrupted')
  );
}

function ensureInitialized(): Promise<void> {
  if (!initializePromise) {
    initializePromise = SocialLogin.initialize({
      google: {
        webClientId: WEB_CLIENT_ID,
        ...(IOS_CLIENT_ID ? { iOSClientId: IOS_CLIENT_ID } : {}),
        mode: 'online',
      },
    }).then(() => undefined);
    initializePromise.catch(() => {
      // Allow a retry on the next attempt instead of caching the failure.
      initializePromise = null;
    });
  }
  return initializePromise;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function createRawNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
}

export async function signInWithNativeGoogle(
  options: NativeGoogleSignInOptions = {},
): Promise<NativeGoogleSignInResult> {
  if (!isNativeGoogleSignInConfigured()) {
    throw new Error('Native Google sign-in is not configured on this platform.');
  }

  await ensureInitialized();

  // Google receives the hash in the native request and places it in the ID
  // token. Supabase receives the raw value and hashes it before comparison.
  const nonce = createRawNonce();
  const nonceDigest = await sha256Hex(nonce);

  const response = await SocialLogin.login({
    provider: 'google',
    options: {
      nonce: nonceDigest,
      ...(options.forceAccountSelection ? { forcePrompt: true } : {}),
    },
  });

  const result =
    (response as unknown as { result?: Record<string, unknown> }).result ?? {};
  const profile =
    result.profile && typeof result.profile === 'object'
      ? (result.profile as Record<string, unknown>)
      : {};

  const idToken =
    readString(result.idToken) ||
    readString((result.accessToken as Record<string, unknown> | undefined)?.idToken) ||
    readString(result.token);
  const accessToken =
    readString((result.accessToken as Record<string, unknown> | undefined)?.token) ||
    readString(result.accessToken);

  if (!idToken) {
    throw new Error('Google sign-in did not return an identity token.');
  }

  return {
    idToken,
    nonce,
    accessToken,
    email: readString(profile.email),
    givenName: readString(profile.givenName) ?? readString(profile.given_name),
    familyName: readString(profile.familyName) ?? readString(profile.family_name),
  };
}
